import {
  androidRules,
  iosRules,
  type MobileRule
} from '@deque/accessibility-standards';
import type { v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Checkbox,
  FieldGroup,
  Icon,
  Link,
  Loader
} from '@deque/cauldron-react';
import {
  MobilePlatform,
  resolveEnabledMobileRules
} from '@deque/jazzband-utils';
import type { SettingsSchemaAxeDevtoolsMobile } from '@deque/orgwide-settings-client';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageTitle from '~/common/components/PageTitle';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { useLoadedConfiguration } from '~/common/contexts/Configuration';
import { useProducts } from '~/common/contexts/products';
import { PRODUCT_URLS } from '~/modules/common/constants';
import styles from './Configuration.css';
import Form from './Form';

export const IOS_RULES_DOCS_URL = `${PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].docsSDK}/ios-rules`;
export const ANDROID_RULES_DOCS_URL = `${PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].docsSDK}/android-rules`;

export const byName = (a: MobileRule, b: MobileRule) =>
  a.name.localeCompare(b.name);
const sortedIosRules = Object.values(iosRules).sort(byName);
const sortedAndroidRules = Object.values(androidRules).sort(byName);

interface MobileConfigurationForm {
  allowUserOverrides: boolean;
  // Rule enablement keyed by the rule's SDK value, true = enabled
  ios: Record<string, boolean>;
  android: Record<string, boolean>;
}

// The stored shape is sparse (disabled rules only), so a missing config means
// "all rules enabled". Enablement is derived through the same shared resolver
// the backend reconciliation and the config-drift banner use, so all three
// agree on what "enabled" means for a given org config.
const toFormValues = (
  mobileSettings: SettingsSchemaAxeDevtoolsMobile | null | undefined
): MobileConfigurationForm => {
  const config = mobileSettings ?? {
    iosDisabledRules: { value: [] },
    androidDisabledRules: { value: [] }
  };
  const iosEnabled = new Set(
    resolveEnabledMobileRules(config, MobilePlatform.IOS)
  );
  const androidEnabled = new Set(
    resolveEnabledMobileRules(config, MobilePlatform.ANDROID)
  );

  return {
    allowUserOverrides: mobileSettings?.allowUserOverrides?.value ?? false,
    ios: Object.fromEntries(
      sortedIosRules.map(rule => [rule.value, iosEnabled.has(rule.value)])
    ),
    android: Object.fromEntries(
      sortedAndroidRules.map(rule => [
        rule.value,
        androidEnabled.has(rule.value)
      ])
    )
  };
};

const AxeDevToolsMobileConfiguration: React.FC<{
  product: v2.Product;
}> = ({ product }) => {
  const { t } = useTranslation();
  const analytics = useAxeAccountAnalytics();
  const {
    settings,
    updateSettings,
    saving,
    savingError,
    isEnterpriseAdmin,
    isEnterpriseMember,
    updateSettingsShouldUpdateKey,
    loadingUserEnterpriseSettings
  } = useLoadedConfiguration();
  const pendingSavedEvent = React.useRef<
    Parameters<typeof analytics.mobileRuleConfigSaved>[0] | null
  >(null);
  const { getProductBySlug } = useProducts();
  const { handleSubmit, reset, control, formState } =
    useForm<MobileConfigurationForm>();
  const location = useLocation();

  const mobileSettings = settings.axeDevtoolsMobile;
  // Only allow editing / saving if the user is an enterprise admin
  const isReadOnly = isEnterpriseMember && !isEnterpriseAdmin;
  const disabled = saving || isReadOnly;

  React.useEffect(() => {
    reset(toFormValues(mobileSettings));
  }, [mobileSettings]);

  React.useEffect(() => {
    analytics.mobileRuleConfigViewed({
      role: isEnterpriseAdmin ? 'admin' : 'member'
    });
  }, [isEnterpriseAdmin]);

  React.useEffect(() => {
    if (location.key) {
      updateSettingsShouldUpdateKey(location.key);
    }
  }, [location.key]);

  React.useEffect(() => {
    if (saving || !pendingSavedEvent.current) {
      return;
    }
    if (!savingError) {
      analytics.mobileRuleConfigSaved(pendingSavedEvent.current);
    }
    pendingSavedEvent.current = null;
  }, [saving]);

  const onSubmit = (data: MobileConfigurationForm) => {
    const iosDisabledRules = sortedIosRules
      .map(rule => rule.value)
      .filter(value => !data.ios[value]);
    const androidDisabledRules = sortedAndroidRules
      .map(rule => rule.value)
      .filter(value => !data.android[value]);

    pendingSavedEvent.current = {
      rules_disabled_count_ios: iosDisabledRules.length,
      rules_disabled_count_android: androidDisabledRules.length,
      allow_overrides: data.allowUserOverrides,
      role: isEnterpriseAdmin ? 'admin' : 'member'
    };

    updateSettings({
      axeDevtoolsMobile: {
        allowUserOverrides: {
          value: data.allowUserOverrides,
          type: 'default'
        },
        iosDisabledRules: {
          value: iosDisabledRules,
          type: 'default'
        },
        androidDisabledRules: {
          value: androidDisabledRules,
          type: 'default'
        }
      }
    });
  };

  const renderRuleColumn = (
    platform: 'ios' | 'android',
    label: string,
    rules: MobileRule[],
    docsUrl: string,
    docsLabel: string
  ) => (
    <FieldGroup
      label={label}
      description={
        <>
          <Link href={docsUrl} target="_blank" rel="noopener noreferrer">
            {docsLabel}
          </Link>
          <Icon type="external-link" label={t('opens in new tab')} />
        </>
      }
    >
      {rules.map(rule => (
        <Controller
          key={rule.value}
          control={control}
          name={`${platform}.${rule.value}`}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              id={`mobile-rule-${platform}-${rule.value}`}
              label={rule.name}
              checked={!!value}
              onChange={onChange}
              disabled={disabled}
            />
          )}
        />
      ))}
    </FieldGroup>
  );

  return (
    <>
      <PageTitle
        title={t('Configuration - {{productName}}', {
          productName: getProductBySlug(ProductSlugs.axeDevToolsMobile)?.name
        })}
      />
      <Form
        header={product.name}
        isDirty={formState.isDirty}
        isReadOnly={isReadOnly}
        onSave={handleSubmit(onSubmit)}
        onDiscard={() => {
          analytics.configDiscard(ProductSlugs.axeDevToolsMobile);
          reset();
        }}
      >
        <form>
          {loadingUserEnterpriseSettings ? (
            <Loader label={t('Loading Axe DevTools Mobile settings')} />
          ) : (
            <>
              <h3>{t('Custom rulesets')}</h3>
              <p>
                {t('Select which rules report issues for your organization')}
              </p>
              <Controller
                control={control}
                name="allowUserOverrides"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id="mobile-allow-user-overrides"
                    label={t('Allow users to change')}
                    checked={!!value}
                    onChange={onChange}
                    disabled={disabled}
                  />
                )}
              />
              <div className={styles.ruleColumns}>
                {renderRuleColumn(
                  'ios',
                  'iOS',
                  sortedIosRules,
                  IOS_RULES_DOCS_URL,
                  t('View Rule Overview for iOS in a new tab')
                )}
                {renderRuleColumn(
                  'android',
                  'Android',
                  sortedAndroidRules,
                  ANDROID_RULES_DOCS_URL,
                  t('View Rule Overview for Android in a new tab')
                )}
              </div>
            </>
          )}
        </form>
      </Form>
    </>
  );
};

export default AxeDevToolsMobileConfiguration;
